
import { defineComponent, PropType } from 'vue'

import { SupportValidationData } from '~/types/support'
import ZInput from '../zeal/ZInput'
import SupportDescription from './SupportDescription.vue'

export default defineComponent({
  name: 'SupportForm',
  components: { SupportDescription, ZInput },
  props: {
    authorEmail: {
      type: String,
      required: true
    },
    isSubmittingForm: {
      type: Boolean,
      default: false
    },
    filesToUpload: {
      type: Array as PropType<File[]>,
      default: () => []
    }
  },
  data() {
    return {
      supportHTML: '',
      isProcessingFiles: false
    }
  },
  computed: {
    FORM_FIELDS() {
      return [
        {
          displayName: 'From',
          helperText: 'Primary point of contact for this support request.',
          is: 'ZInput',
          inputBinds: {
            name: 'fromEmail',
            placeholder: '',
            readOnly: true,
            value: this.authorEmail
          },
          classNames: 'col-span-full md:col-span-1',
          handlers: false
        },
        {
          displayName: 'CC',
          helperText: 'Use commas to separate multiple email addresses.',
          is: 'ZInput',
          inputBinds: {
            name: 'ccEmails',
            placeholder: 'Enter email(s)'
          },
          classNames: 'col-span-full md:col-span-1',
          handlers: false
        },
        {
          displayName: 'Subject',
          helperText: '',
          is: 'ZInput',
          inputBinds: {
            name: 'subject',
            placeholder: ''
          },
          classNames: 'col-span-full',
          handlers: false
        },
        {
          displayName: 'Description',
          helperText: '',
          is: 'SupportDescription',
          inputBinds: {
            name: 'body',
            disabled: this.isSubmittingForm,
            isProcessingFiles: this.isProcessingFiles
          },
          classNames: 'col-span-full',
          handlers: {
            input: (newValue: string) => {
              this.supportHTML = newValue
            },
            'files-change': (newFiles: File[]) =>
              this.$emit('files-change', this.filesToUpload.concat(newFiles)),
            'update-files-processing': (newValue: boolean) => (this.isProcessingFiles = newValue)
          }
        }
      ]
    },
    supportText() {
      //? This line is responsible for simply getting textual characters from the HTML string that the rich text editor component provides.
      //? Another way would be to use: new DOMParser().parseFromString(html,"text/html").documentElement.textContent
      //? But the memory overhead of using such task isn't worth it, since its just a minor validation
      //! Intentional ReDoS since better validation performed at backend
      return this.supportHTML.replace(/<[^>]+>/g, '') || ''
    }
  },
  methods: {
    /**
     * Deletes a given file via its index from {@link filesToUpload}
     *
     * @param {number} fileIndex - Index of the file to delete
     * @returns {boolean}
     */
    removeFile(fileIndex: number): void {
      this.$emit(
        'files-change',
        this.filesToUpload.filter((_val, index) => fileIndex !== index)
      )
    },

    /**
     * Validates input data and shows errors as required.
     *
     * @param {SupportValidationData} validationData - Data to validate
     *
     * @returns {boolean} - Whether data is valid (true) or not (false).
     */
    validateInputs(valdiationData: SupportValidationData): boolean {
      if (!valdiationData.fromEmail) {
        this.$toast.danger('A primary email address is required.')
        return false
      }
      const multiEmailInput = this.$refs['multiEmailInput'] as HTMLInputElement
      multiEmailInput.value = valdiationData.fromEmail
      if (!multiEmailInput.checkValidity()) {
        this.$toast.danger('The primary email address is invalid.')
        multiEmailInput.value = ''
        return false
      }
      multiEmailInput.value = ''
      if (valdiationData.ccEmails) {
        multiEmailInput.value = valdiationData.ccEmails
        if (!multiEmailInput.checkValidity()) {
          this.$toast.danger('One or more email addresses added in CC are invalid.')
          multiEmailInput.value = ''
          return false
        }
        multiEmailInput.value = ''
      }
      if (!valdiationData.subject || valdiationData.subject.length < 5) {
        this.$toast.danger('Subject line should be 5 characters or more.')
        return false
      }
      if (valdiationData.subject.length > 250) {
        this.$toast.danger('Subject line should be 250 characters or less.')
        return false
      }
      if (!valdiationData.supportText.length || valdiationData.supportText.length < 20) {
        this.$toast.danger('Description should be 20 characters or more.')
        return false
      }
      return true
    },

    /**
     * Submits the form data to the parent component
     *
     * @param {Event} e - Form submit event
     * @returns {void}
     */
    submitForm(e: Event): void {
      const dataToValidate: Record<string, unknown> = {}
      const formData = new FormData(e.target as HTMLFormElement)
      const SKIPPED_INPUTS = ['multiEmailInput']

      formData.forEach((value, key) => {
        if (!SKIPPED_INPUTS.includes(key)) {
          dataToValidate[key] = value
        }
      })
      dataToValidate['supportText'] = this.supportText
      dataToValidate['supportHTML'] = this.supportHTML
      if (this.validateInputs(dataToValidate as SupportValidationData)) {
        this.$emit('submit', dataToValidate)
      }
    }
  }
})
